import { z } from "zod";
import type { TaskType } from "@remi/enums/task-type";

export const ProjectMilestoneValues = [
  "ASSIGNMENT",
  "READY_TO_ACCEPT",
  "PERMITTING",
  "SCHEDULING",
  "READY_TO_START",
  "PRODUCTION",
  "READY_TO_CLOSE",
  "WARRANTY",
] as const;

export const ProjectMilestoneSchema = z.enum(ProjectMilestoneValues);
export const ProjectMilestone = ProjectMilestoneSchema.enum;
export type ProjectMilestone = (typeof ProjectMilestoneValues)[number];

// Recording work start is time-critical, so it jumps ahead of the natural milestone order.
export const ProjectMilestonePriority: readonly ProjectMilestone[] = [
  ProjectMilestone.READY_TO_START,
  ProjectMilestone.WARRANTY,
  ...ProjectMilestoneValues.filter(
    (milestone) => milestone !== ProjectMilestone.READY_TO_START && milestone !== ProjectMilestone.WARRANTY,
  ),
];

export const ProjectMilestoneDisplayNames = {
  [ProjectMilestone.ASSIGNMENT]: "Assignment",
  [ProjectMilestone.READY_TO_ACCEPT]: "Ready to accept",
  [ProjectMilestone.PERMITTING]: "Permitting",
  [ProjectMilestone.SCHEDULING]: "Scheduling",
  [ProjectMilestone.READY_TO_START]: "Ready to start",
  [ProjectMilestone.PRODUCTION]: "Production",
  [ProjectMilestone.READY_TO_CLOSE]: "Ready to close",
  [ProjectMilestone.WARRANTY]: "Warranty",
} as const satisfies Record<ProjectMilestone, string>;

/**
 * Defines the mapping of project milestones to task types.
 * If a project has an open task of a type in this map, it is considered to be at that milestone.
 * It will choose the earliest milestone in the order of this map.
 */
export const ProjectMilestoneTaskMap: Readonly<Record<ProjectMilestone, TaskType[]>> = {
  [ProjectMilestone.ASSIGNMENT]: ["CONFIRM_CONTRACTOR", "CREATE_WORK_ORDER"],
  [ProjectMilestone.READY_TO_ACCEPT]: ["REVIEW_AND_ACCEPT_WORK_ORDER"],
  [ProjectMilestone.PERMITTING]: ["PERMIT_COMPLETED", "APPROVED_BY_AHJ"],
  [ProjectMilestone.SCHEDULING]: ["INSTALL_SCHEDULED", "COLOR_SELECTION", "ORDER_MATERIALS"],
  [ProjectMilestone.READY_TO_START]: ["INSTALL_STARTED"],
  [ProjectMilestone.PRODUCTION]: [
    "INSTALL_COMPLETED",
    "UPLOADED_COMPLETED_WORK_PHOTOS",
    "SITE_VISIT_DETAILS_RECORDED",
    "FINAL_INSPECTION_SCHEDULED",
    "FINAL_INSPECTION_COMPLETED",
  ],
  [ProjectMilestone.READY_TO_CLOSE]: [
    "REVIEW_AND_CLOSE_WORK_ORDER",
    "VERIFY_CONTRACTOR_COSTS",
    "CONFIRM_PROJECT_IS_RTI",
    // "INVOICE_PAID",
    // "CONTRACTOR_PAID",
  ],
  [ProjectMilestone.WARRANTY]: [
    "CUSTOMER_COMPLAINT",
    "CUSTOMER_COMPLAINT_TIER_2",
    "CUSTOMER_COMPLAINT_TIER_3",
    "WORKMANSHIP_ISSUE",
    "ROOF_LEAK",
  ],
};
